import { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import toastService from '../../../../../utils/toastService';
import {
    useGetWarrantyAuditCriteriaQuery,
    useCreateWarrantyAuditCriteriaMutation,
    useUpdateWarrantyAuditCriteriaMutation,
    useDeleteWarrantyAuditCriteriaMutation,
    useCreateWarrantyAuditSubCriteriaMutation,
    useUpdateWarrantyAuditSubCriteriaMutation,
    useDeleteWarrantyAuditSubCriteriaMutation,
    useCreateWarrantyAuditEvaluationMutation,
    useUpdateWarrantyAuditEvaluationMutation,
    useDeleteWarrantyAuditEvaluationMutation
} from '../../../../../api/endpoints/warrantyAudit';

// Diyalog tipleri: kriter / alt kriter / değerlendirme
export const DIALOG_TYPE = {
    CRITERIA: 'criteria',
    SUB_CRITERIA: 'subCriteria',
    EVALUATION: 'evaluation'
};

const useCriteriaManagement = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [dialog, setDialog] = useState({ visible: false, type: null, mode: 'create', data: null, parentId: null });

    const { data: criteria = [], isLoading, isFetching, refetch } = useGetWarrantyAuditCriteriaQuery({
        languageId,
        includeInactive: true,
        includeTranslations: true // düzenleme diyaloğu 6 dili tek istekte doldurabilsin
    });

    const [createCriteria, { isLoading: isCreatingCriteria }] = useCreateWarrantyAuditCriteriaMutation();
    const [updateCriteria, { isLoading: isUpdatingCriteria }] = useUpdateWarrantyAuditCriteriaMutation();
    const [deleteCriteria] = useDeleteWarrantyAuditCriteriaMutation();

    const [createSubCriteria, { isLoading: isCreatingSub }] = useCreateWarrantyAuditSubCriteriaMutation();
    const [updateSubCriteria, { isLoading: isUpdatingSub }] = useUpdateWarrantyAuditSubCriteriaMutation();
    const [deleteSubCriteria] = useDeleteWarrantyAuditSubCriteriaMutation();

    const [createEvaluation, { isLoading: isCreatingEval }] = useCreateWarrantyAuditEvaluationMutation();
    const [updateEvaluation, { isLoading: isUpdatingEval }] = useUpdateWarrantyAuditEvaluationMutation();
    const [deleteEvaluation] = useDeleteWarrantyAuditEvaluationMutation();

    const isSaving =
        isCreatingCriteria || isUpdatingCriteria || isCreatingSub || isUpdatingSub || isCreatingEval || isUpdatingEval;

    const openCreate = useCallback((type, parentId = null) => {
        setDialog({ visible: true, type, mode: 'create', data: null, parentId });
    }, []);

    const openEdit = useCallback((type, data, parentId = null) => {
        setDialog({ visible: true, type, mode: 'edit', data, parentId });
    }, []);

    const closeDialog = useCallback(() => {
        setDialog({ visible: false, type: null, mode: 'create', data: null, parentId: null });
    }, []);

    const mutationsByType = useMemo(
        () => ({
            [DIALOG_TYPE.CRITERIA]: { create: createCriteria, update: updateCriteria },
            [DIALOG_TYPE.SUB_CRITERIA]: { create: createSubCriteria, update: updateSubCriteria },
            [DIALOG_TYPE.EVALUATION]: { create: createEvaluation, update: updateEvaluation }
        }),
        [createCriteria, updateCriteria, createSubCriteria, updateSubCriteria, createEvaluation, updateEvaluation]
    );

    const handleSave = useCallback(
        async (type, payload, mode) => {
            try {
                const fns = mutationsByType[type];
                // POST/PUT isteklerinde languageId body içinde gönderilir (validasyon mesajları için)
                const payloadWithLanguage = { ...payload, languageId };
                if (mode === 'edit') {
                    await fns.update(payloadWithLanguage).unwrap();
                    toastService.success(th('warrantyAudit.toast.updateSuccess') || 'Kayıt güncellendi');
                } else {
                    await fns.create(payloadWithLanguage).unwrap();
                    toastService.success(th('warrantyAudit.toast.createSuccess') || 'Kayıt oluşturuldu');
                }
                await refetch();
                closeDialog();
            } catch (error) {
                toastService.error(th('warrantyAudit.toast.saveError') || 'Kayıt sırasında hata oluştu');
                throw error;
            }
        },
        [mutationsByType, refetch, closeDialog, th, languageId]
    );

    const handleDelete = useCallback(
        async (type, id) => {
            const deleters = {
                [DIALOG_TYPE.CRITERIA]: deleteCriteria,
                [DIALOG_TYPE.SUB_CRITERIA]: deleteSubCriteria,
                [DIALOG_TYPE.EVALUATION]: deleteEvaluation
            };
            try {
                // DELETE isteklerinde languageId query string olarak gönderilir (hata mesajı için)
                await deleters[type]({ id, languageId }).unwrap();
                toastService.success(th('warrantyAudit.toast.deleteSuccess') || 'Kayıt pasife alındı');
                await refetch();
            } catch (error) {
                toastService.error(th('warrantyAudit.toast.deleteError') || 'Silme sırasında hata oluştu');
            }
        },
        [deleteCriteria, deleteSubCriteria, deleteEvaluation, refetch, th, languageId]
    );

    return {
        criteria,
        isLoading: isLoading || isFetching,
        isSaving,
        dialog,
        openCreate,
        openEdit,
        closeDialog,
        handleSave,
        handleDelete
    };
};

export default useCriteriaManagement;
